import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import {
  getChannelHealth,
  getSendConfig,
  getSystemHealth,
  type SystemHealthDailyCost,
} from '../../lib/api.js';
import {
  type DrawerSection,
  deriveAlerts,
  deriveSendPosture,
  deriveStatus,
  fmtUsd,
  type StatusLevel,
} from '../../lib/health-status.js';
import { useRealtimeConnected } from '../../lib/realtime.js';

const DOT_CLASS: Record<StatusLevel, string> = {
  live: 'app-dot-live',
  degraded: 'app-dot-amber',
  down: 'app-dot-red',
  unknown: 'app-dot-unknown',
};

/**
 * R9 — the always-on status cluster in the topbar: a 4-state status pill, the
 * send-posture pill, and a cost pill with a click-disclosure sparkline. The
 * status + send pills open the health drawer; the cost pill discloses its own
 * 14-day trend. All three read the SAME deriders the alert dock + drawer use, so
 * the badge can't disagree with the dock — and a failed health feed shows
 * "Status unknown" rather than a silent green.
 */
export function StatusCluster({
  onOpenDrawer,
}: {
  onOpenDrawer: (section?: DrawerSection) => void;
}) {
  const channelQ = useQuery({
    queryKey: ['channel-health'],
    queryFn: ({ signal }) => getChannelHealth({ signal }),
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
  const systemQ = useQuery({
    queryKey: ['system-health'],
    queryFn: ({ signal }) => getSystemHealth({ signal }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const sendQ = useQuery({
    queryKey: ['send-config'],
    queryFn: ({ signal }) => getSendConfig({ signal }),
    staleTime: 60_000,
  });
  const sseConnected = useRealtimeConnected();

  const alerts = deriveAlerts(channelQ.data, systemQ.data);
  const status = deriveStatus(alerts, {
    errored: channelQ.isError || systemQ.isError,
    sseConnected,
    // R9 review #2 — require BOTH feeds before leaving "unknown". With OR, a
    // fast system-health + a still-pending channel-health would flash "live"
    // while a disconnected channel (the loudest condition) isn't reflected yet.
    loaded: channelQ.isSuccess && systemQ.isSuccess,
  });
  const sendPosture = deriveSendPosture(sendQ.data);
  const cost = systemQ.data?.cost;

  return (
    <div className="app-status-cluster" data-testid="status-cluster">
      <button
        type="button"
        className="app-pill app-pill-status"
        data-testid="status-pill"
        data-level={status.level}
        title="Open the health drawer: channels, worker, failures, cost, backups, connection"
        onClick={() => onOpenDrawer()}
      >
        <span
          className={`app-dot ${DOT_CLASS[status.level]}${status.breathing ? ' app-dot-breathe' : ''}`}
        />
        <span>{status.label}</span>
      </button>

      {sendPosture ? (
        <button
          type="button"
          className={`app-pill app-pill-send${sendPosture.armed ? ' is-armed' : ''}`}
          data-testid="send-posture-pill"
          title="Your live sending posture (Setup → Sending & automation)"
          onClick={() => onOpenDrawer()}
        >
          <span className="app-dot app-dot-send" />
          <span>{sendPosture.label}</span>
        </button>
      ) : null}

      {cost ? (
        <CostPill
          today={cost.today_usd}
          yesterday={cost.yesterday_usd}
          avg={cost.trailing_avg_usd}
          spike={cost.spike}
          days={cost.days}
        />
      ) : null}
    </div>
  );
}

function CostPill({
  today,
  yesterday,
  avg,
  spike,
  days,
}: {
  today: number;
  // R9 review #1 — yesterday + avg come from the SERVER (cost.yesterday_usd /
  // trailing_avg_usd) so the popover matches the alert dock + health drawer.
  // Recomputing client-side over the sparse `days` group-by array diverged
  // (it included today in the avg and `[len-2]` could be two-days-ago).
  yesterday: number;
  avg: number;
  spike: boolean;
  days: SystemHealthDailyCost[];
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const popId = useId();

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const recent = days.slice(-14);

  return (
    <div ref={rootRef} className="app-cost-wrap">
      <button
        type="button"
        className="app-pill app-pill-cost"
        data-testid="cost-pill"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        title="Today's analysis spend — click for the 14-day trend"
        onClick={() => setOpen((v) => !v)}
      >
        {spike ? <span aria-hidden>⚠️</span> : null}
        <span>{fmtUsd(today)} today</span>
      </button>
      {open ? (
        <div id={popId} className="app-cost-pop" data-testid="cost-pop">
          <div className="app-cost-figures">
            <span>
              Today <strong>{fmtUsd(today)}</strong>
            </span>
            <span>
              Yesterday <strong>{fmtUsd(yesterday)}</strong>
            </span>
            <span>
              14-day avg <strong>{fmtUsd(avg)}</strong>
            </span>
          </div>
          <Sparkline days={recent} />
          {spike ? (
            <p className="app-cost-note">⚠️ Today is a spike vs the 14-day average.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Sparkline({ days }: { days: SystemHealthDailyCost[] }) {
  if (days.length === 0) return null;
  const w = 220;
  const h = 44;
  const max = Math.max(...days.map((d) => d.cost_usd), 0.0001);
  const bw = w / days.length;
  return (
    <svg
      className="app-sparkline"
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      role="img"
      aria-label={`Daily analysis spend over the last ${days.length} days`}
    >
      {days.map((d, i) => {
        const bh = Math.max(1, (d.cost_usd / max) * (h - 4));
        return (
          <rect
            key={d.day}
            x={i * bw + 1}
            y={h - bh}
            width={Math.max(1, bw - 2)}
            height={bh}
            rx={1}
            className="app-spark-bar"
          />
        );
      })}
    </svg>
  );
}
